import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Button, Flex, Table, Tag, Tooltip, message, Spin } from 'antd'
import type { TableProps } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import Title from 'antd/es/typography/Title'
import { type Pixel, pixelPlatformOptions } from '@/data'
import { getPixels } from '@/api/lop/pixels/getPixels'
import { deletePixel } from '@/api/lop/pixels/deletePixel'
import { AuthContext } from '@/contexts/AuthContext'
import { hasPermission } from '@/auth/access'
import { resolveBackendSort } from '@/utils/backendSort'
import { SearchInput } from '@/components/SearchInput'
import { getErrorMessage } from '@/utils/getErrorMessage'

export const PixelsList: React.FC = () => {
  const navigate = useNavigate()
  const auth = useContext(AuthContext)!
  const [pixels, setPixels] = useState<Pixel[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  })
  const [sortField, setSortField] = useState('-created_at')
  const [credentialTypeFilter, setCredentialTypeFilter] = useState<string>()
  const [search, setSearch] = useState('')

  const hasFetched = useRef(false)
  const canReadPixels = hasPermission(auth.user, 'lop:ui:action:read:pixels')
  const canCreatePixels = hasPermission(
    auth.user,
    'lop:ui:action:create:pixels',
  )
  const canUpdatePixels = hasPermission(
    auth.user,
    'lop:ui:action:update:pixels',
  )
  const canDeletePixels = hasPermission(
    auth.user,
    'lop:ui:action:delete:pixels',
  )

  const columnSortKeys = {
    name: 'name',
    // TODO: uncomment when backend supports sorting by credential.credential_type
    // platform: 'credential.credential_type',
    created_at: 'created_at',
  } as const

  const fetchPixels = useCallback(
    async (
      page = 1,
      sort = sortField,
      credentialType = credentialTypeFilter,
      query = search,
    ) => {
      try {
        setLoading(true)
        setError(null)
        const filters: { key: string; value: string }[] = []
        if (credentialType) {
          filters.push({
            key: 'credential.credential_type',
            value: credentialType,
          })
        }
        if (query) {
          filters.push({ key: 'name', value: query })
        }
        const { data, meta } = await getPixels({
          page,
          sort,
          ...(filters.length ? { filters } : {}),
        })
        setPixels(data)
        setPagination({
          current: meta.current_page,
          pageSize: meta.per_page,
          total: meta.total,
        })
      } catch (err) {
        setError('Failed to load pixels')
        console.error(err)
      } finally {
        setLoading(false)
      }
    },
    [sortField, credentialTypeFilter, search],
  )

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value)
      fetchPixels(1, sortField, credentialTypeFilter, value)
    },
    [fetchPixels, sortField, credentialTypeFilter],
  )

  useEffect(() => {
    if (hasFetched.current) return
    if (!canReadPixels) return
    hasFetched.current = true
    fetchPixels()
  }, [canReadPixels, fetchPixels])

  const handleDelete = async (id: number) => {
    try {
      await deletePixel(String(id))
      message.success('Pixel deleted')
      fetchPixels(pagination.current)
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to delete pixel'))
      console.error(err)
    }
  }

  const columns: TableProps<Pixel>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text, record) =>
        canUpdatePixels ? (
          <Link to={`/pixels/${record.id}/edit`}>{text}</Link>
        ) : (
          text
        ),
    },
    {
      title: 'Platform',
      key: 'platform',
      // TODO: uncomment when backend supports sorting & filtering by credential.credential_type
      // sorter: true,
      // filters: pixelPlatformOptions.map((o) => ({
      //   text: o.label,
      //   value: o.value,
      // })),
      // filterMultiple: false,
      render: (_, record) => {
        const type = record.credential?.credential_type
        return (
          pixelPlatformOptions.find((o) => o.value === type)?.label ||
          type ||
          '—'
        )
      },
    },
    {
      title: 'Pixel ID',
      dataIndex: 'pixel_id',
      key: 'pixel_id',
      render: (id: string) => <Tag>{id}</Tag>,
    },
    {
      title: 'Partner',
      key: 'partner',
      sorter: false,
      render: (_, record) => record.partner?.name ?? '—',
    },
    {
      title: 'Events',
      key: 'events',
      sorter: false,
      render: (_, record) => record.event_mappings.length,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (value: string) =>
        value
          ? new Date(value).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Flex gap="middle">
          {canUpdatePixels ? (
            <Tooltip title="Edit">
              <Link to={`/pixels/${record.id}/edit`}>
                <EditOutlined style={{ fontSize: 16 }} />
              </Link>
            </Tooltip>
          ) : null}
          {canDeletePixels ? (
            <Tooltip title="Delete">
              <DeleteOutlined
                style={{ fontSize: 16, color: '#ff4d4f', cursor: 'pointer' }}
                onClick={() => handleDelete(record.id)}
              />
            </Tooltip>
          ) : null}
        </Flex>
      ),
    },
  ]

  if (error) {
    return (
      <div>
        <Title level={4}>Pixels</Title>
        <p style={{ color: '#ff4d4f' }}>{error}</p>
        <Button onClick={() => fetchPixels()}>Retry</Button>
      </div>
    )
  }

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Pixels
        </Title>
        <Flex gap="small" align="center">
          <SearchInput onSearch={handleSearch} />
          {canCreatePixels ? (
            <Button type="primary" onClick={() => navigate('/pixels/new')}>
              New Pixel
            </Button>
          ) : null}
        </Flex>
      </Flex>
      {canReadPixels ? (
        <Spin spinning={loading}>
          <Table<Pixel>
            rowKey="id"
            columns={columns}
            dataSource={pixels}
            pagination={pagination}
            onChange={(p, filters, sorter) => {
              const sort = resolveBackendSort(
                sorter,
                columnSortKeys,
                '-created_at',
              )
              const credentialType = filters.platform?.[0] as string | undefined
              setSortField(sort)
              setCredentialTypeFilter(credentialType)
              fetchPixels(p.current, sort, credentialType, search)
            }}
          />
        </Spin>
      ) : null}
    </div>
  )
}
